import { Search } from 'lucide-react';
import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react';

import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/shared/utils/cn';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onValueChange: (next: string) => void;
  /** Fires after `debounceMs` of no typing; omitted = no debounced callback. */
  onDebouncedChange?: (next: string) => void;
  debounceMs?: number;
  label: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, onDebouncedChange, debounceMs = 300, label, className, id, ...rest },
  ref,
) {
  const debounced = useDebounce(value, debounceMs);

  useEffect(() => {
    onDebouncedChange?.(debounced);
  }, [debounced, onDebouncedChange]);

  const [internal, setInternal] = useState(value);
  useEffect(() => setInternal(value), [value]);

  return (
    <label className={cn('relative block w-full', className)} htmlFor={id}>
      <span className="sr-only">{label}</span>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-fg-subtle"
        strokeWidth={2}
      />
      <input
        ref={ref}
        id={id}
        type="search"
        value={internal}
        onChange={(e) => {
          const next = e.target.value;
          setInternal(next);
          onValueChange(next);
        }}
        className={cn(
          'h-9 w-full rounded-sm border border-border-strong bg-surface pl-10 pr-3 text-sm text-fg',
          'placeholder:text-fg-subtle',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        )}
        {...rest}
      />
    </label>
  );
});
